import type { PortInfo } from "@/types/booking";

type PortLike = Pick<PortInfo, "locode"> & { name?: string | null };

export type PortNameLookup = ReadonlyMap<string, string> | Record<string, string>;

/** UN/LOCODE: two-letter country + three-letter location. */
export function isLocode(value: string | null | undefined): boolean {
  return !!value && /^[A-Za-z]{5}$/.test(value.trim());
}

function portName(port: PortLike): string {
  return port.name?.trim() || port.locode;
}

function lookupName(names: PortNameLookup | undefined, locode: string): string | undefined {
  if (!names) return undefined;
  if (names instanceof Map) return names.get(locode) ?? names.get(locode.toUpperCase());
  const record = names as Record<string, string>;
  return record[locode] ?? record[locode.toUpperCase()];
}

/** Port label — always the name, with an optional locode suffix. */
export function formatPortLabel(port: PortLike, showLocodes: boolean): string {
  const name = portName(port);
  if (!showLocodes || !port.locode || name === port.locode) return name;
  return `${name} (${port.locode})`;
}

/**
 * Pre-carriage sub-label shown under origin, e.g. `via Barge → Shekou`.
 * Returns null when the line is direct (no POL and no mode of transport).
 */
export function formatPreCarriage(
  pol?: string | null,
  mot?: string | null,
): string | null {
  const cleanedPol = pol?.trim() ?? "";
  const cleanedMot = mot?.trim() ?? "";
  if (!cleanedPol && !cleanedMot) return null;
  if (cleanedPol && cleanedMot) return `via ${cleanedMot} → ${cleanedPol}`;
  return `via ${cleanedMot || cleanedPol}`;
}

/** Format pre-carriage using a port object and the viewer's locode preference. */
export function formatPreCarriageLabel(
  pol: PortLike | null | undefined,
  mot: string | null | undefined,
  showLocodes: boolean,
): string | null {
  return formatPreCarriage(pol ? formatPortLabel(pol, showLocodes) : null, mot);
}

/** Route label — always port names, with optional locode suffixes. */
export function formatRouteLabel(
  origin: PortLike,
  destination: PortLike,
  showLocodes: boolean,
): string {
  return `${formatPortLabel(origin, showLocodes)} → ${formatPortLabel(destination, showLocodes)}`;
}

const LANE_SPLIT = /\s*(?:→|->|-)\s*/;

/** Parse `CNNGB→ILASH` / `CNNGB-ILASH` into origin + destination locodes. */
export function splitLaneKey(
  laneKey: string | null | undefined,
): { origin: string; destination: string } | null {
  if (!laneKey?.trim()) return null;
  const parts = laneKey.trim().split(LANE_SPLIT);
  if (parts.length !== 2) return null;
  const origin = parts[0].trim().toUpperCase();
  const destination = parts[1].trim().toUpperCase();
  if (!isLocode(origin) || !isLocode(destination)) return null;
  return { origin, destination };
}

/** Collect locodes that appear as `ORIGIN→DEST` / `ORIGIN-DEST` pairs. */
export function extractLaneLocodes(
  ...texts: (string | null | undefined)[]
): string[] {
  const found = new Set<string>();
  const pair = /\b([A-Za-z]{5})\s*(?:→|->|-)\s*([A-Za-z]{5})\b/g;
  for (const text of texts) {
    if (!text) continue;
    const split = splitLaneKey(text);
    if (split) {
      found.add(split.origin);
      found.add(split.destination);
      continue;
    }
    for (const match of text.matchAll(pair)) {
      found.add(match[1].toUpperCase());
      found.add(match[2].toUpperCase());
    }
  }
  return [...found];
}

/** Lane label — port names, with optional locode suffixes from user prefs. */
export function formatLaneKey(
  laneKey: string,
  showLocodes: boolean,
  names?: PortNameLookup,
): string {
  const parts = splitLaneKey(laneKey);
  if (!parts) {
    return laneKey.replace(/\s*->\s*/g, "→").replace(/\s*-\s*/, "→");
  }
  return formatRouteLabel(
    { locode: parts.origin, name: lookupName(names, parts.origin) ?? null },
    { locode: parts.destination, name: lookupName(names, parts.destination) ?? null },
    showLocodes,
  );
}
